import {
  getValueAsLiteralBoolean,
  getValueAsLiteralString,
  OpaqueSparqlResults,
} from '../../../../../helpers/sparqlResultHelpers'
import { EMPTY_TREE_DATA, no_parent, NO_PARENT, TreeData, TreeItem, TreeNode } from './tree-data'
import { item_id, node_id } from '../common'

const ERROR_TREE_DATA: TreeData = {
  rootNodeIds: ['err'],
  nodesById: { err: { id: 'err', itemId: 'err', childrenIds: [], parentId: NO_PARENT } },
  itemsById: {
    err: {
      id: 'err',
      uri: 'err',
      label: { text: 'Error in tree data', color: 'grey' },
      icon: { name: 'fa-circle-exclamation', color: 'grey' },
      rightLabel: {},
      rightIcon: {},
      defaultChecked: false,
      defaultToggles: {},
      rowResults: {}
    },
  },
}

export function createTreeDataFromSparqlResults(data: OpaqueSparqlResults | undefined): TreeData {
  if (!data) return EMPTY_TREE_DATA

  try {
    const { itemsById, parentsByChildId, childrenByParentId } = bindingsToItems(data)
    const { nodesById, rootNodeIds } = createNodes(itemsById, childrenByParentId, parentsByChildId)

    return {
      rootNodeIds,
      nodesById,
      itemsById
    }
  } catch (e) {
    console.error("Error creating tree data", e)
    return ERROR_TREE_DATA
  }
}

function bindingsToItems(data: OpaqueSparqlResults) {
  const itemsById: Record<item_id, TreeItem> = {}
  const parentsByChildId = new Map<item_id, (item_id | no_parent)[]>()
  const childrenByParentId = new Map<item_id | no_parent, item_id[]>()

  for (const binding of data.results.bindings) {
    if (!binding.uri) {
      throw new Error('Tree++ query result binding has missing uri!')
    }

    // URI need not be an actual URI (it may be a literal)
    const uri = binding.uri.value

    const treeItem: TreeItem = {
      id: uri,
      defaultChecked: binding.defaultChecked ? getValueAsLiteralBoolean(binding.defaultChecked) : false,
      defaultToggles: defaultTogglesFromBinding(binding),
      uri: uri,
      label: {
        text: binding.label ? getValueAsLiteralString(binding.label) : undefined,
        color: binding.color ? getValueAsLiteralString(binding.color) : undefined
      },
      icon: {
        name: binding.icon ? getValueAsLiteralString(binding.icon) : undefined,
        color: binding.iconColor ? getValueAsLiteralString(binding.iconColor) : undefined,
        size: binding.iconSize ? getValueAsLiteralString(binding.iconSize) : undefined,
      },
      rightLabel: {
        text: binding.rightLabel ? getValueAsLiteralString(binding.rightLabel) : undefined,
        color: binding.rightLabelColor ? getValueAsLiteralString(binding.rightLabelColor) : undefined,
      },
      rightIcon: {
        name: binding.rightIcon ? getValueAsLiteralString(binding.rightIcon) : undefined,
        color: binding.rightIconColor ? getValueAsLiteralString(binding.rightIconColor) : undefined,
        size: binding.rightIconSize ? getValueAsLiteralString(binding.rightIconSize) : undefined,
      },
      tooltip: binding.tooltip ? getValueAsLiteralString(binding.tooltip) : undefined,
      canDropInto: binding.canDropInto ? getValueAsLiteralBoolean(binding.canDropInto) : undefined,
      rowResults: binding
    }

    const parentId = binding.parentUri ? binding.parentUri.value : NO_PARENT
    if (!parentsByChildId.has(treeItem.id)) parentsByChildId.set(treeItem.id, [])
    parentsByChildId.get(treeItem.id)!.push(parentId)

    if (!childrenByParentId.has(parentId)) childrenByParentId.set(parentId, [])
    childrenByParentId.get(parentId)!.push(treeItem.id)

    itemsById[treeItem.id] = treeItem
  }
  
  return { itemsById, parentsByChildId, childrenByParentId }
}

const DEFAULT_TOGGLED_PREFIX = 'defaultToggled_'

function defaultTogglesFromBinding(binding: Record<string, unknown>): Record<string, boolean> {
  const defaultToggles: Record<string, boolean> = {}
  for (const key in binding) {
    if (!key.startsWith(DEFAULT_TOGGLED_PREFIX)) continue
    const value = binding[key]
    if (!value) continue
    defaultToggles[key.slice(DEFAULT_TOGGLED_PREFIX.length)] = getValueAsLiteralBoolean(value as never)
  }
  return defaultToggles
}

function createNodes(
  itemsById: Record<item_id, TreeItem>,
  childrenByParentId: Map<(item_id | no_parent), item_id[]>,
  parentsByChildId: Map<item_id, (item_id | no_parent)[]>
) {
  const nodesById: Record<node_id, TreeNode> = {}

  const itemIdOccurrences: Record<item_id, number> = {}
  function generateNodeId(itemId: item_id) {
    if (!(itemId in itemIdOccurrences)) {
      itemIdOccurrences[itemId] = 0
    } else {
      itemIdOccurrences[itemId] += 1
    }

    return `${itemId}:${itemIdOccurrences[itemId]}`
  }

  function createNodesRecurse(
    itemId: item_id,
    parentNodeId: node_id | no_parent,
    visited = new Set<item_id>()
  ): node_id {
    if (visited.has(itemId)) {
      throw new Error(`Cycle detected in tree: ${[...visited, itemId].join(' -> ')}`);
    }

    const newVisited = new Set(visited)
    newVisited.add(itemId)

    const nodeId = generateNodeId(itemId)
    const node: TreeNode = {
      id: nodeId,
      itemId,
      parentId: parentNodeId,
      childrenIds: [],
    }
    nodesById[nodeId] = node

    for (const childItemId of childrenByParentId.get(itemId) ?? []) {
      const childNodeId = createNodesRecurse(childItemId, nodeId, newVisited)
      node.childrenIds.push(childNodeId)
    }

    return nodeId
  }

  const rootNodeIds: node_id[] = []
  for (const itemId of Object.keys(itemsById)) {
    const parentIds = parentsByChildId.get(itemId)
    const hasRootNodeInstance = parentIds?.some(id => id === NO_PARENT) ?? false
    const hasKnownParentInTree = parentIds?.some(id => id !== NO_PARENT && id in itemsById) ?? false

    if (!hasRootNodeInstance && hasKnownParentInTree) {
      continue
    }

    const rootNodeId = createNodesRecurse(itemId, NO_PARENT)
    rootNodeIds.push(rootNodeId)
  }

  return { rootNodeIds, nodesById }
}
